import React, { useState, useEffect, useRef, useMemo } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { heroSlides, heroSettings } from "../../../data/slides";
import useEmblaCarousel from "embla-carousel-react";
import { AnimatePresence, motion } from "framer-motion";

const HeroSection = ({ currentTheme, onThemeChange }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const autoplayTimer = useRef(null);

  const videoContent = heroSlides;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });

  useEffect(() => {
    setIsLoaded(true);
    const tick = () => {
      if (!emblaApi || !isPlaying) return;
      emblaApi.scrollNext();
    };
    autoplayTimer.current = setInterval(
      tick,
      heroSettings?.intervalMs || 10000
    );
    return () => clearInterval(autoplayTimer.current);
  }, [emblaApi, isPlaying]);

  useEffect(() => {
    const currentVideo = videoContent?.[currentVideoIndex];
    if (currentVideo && currentVideo?.theme !== currentTheme) {
      onThemeChange(currentVideo?.theme);
    }
  }, [currentVideoIndex, onThemeChange, currentTheme, videoContent]);

  const handleVideoSelect = (index) => {
    emblaApi?.scrollTo(index);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 2000);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentVideo = videoContent?.[currentVideoIndex];

  // Framer Motion variants for per-letter blur dissolve (random order)
  const letters = (currentVideo?.title || "").split("");
  const titleKey = currentVideo?.id ?? currentVideoIndex;
  const shuffledOrder = useMemo(() => {
    const arr = Array.from({ length: letters.length }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [titleKey, letters.length]);
  const indexToDelay = useMemo(() => {
    const map = new Map();
    shuffledOrder.forEach((originalIndex, position) => {
      map.set(originalIndex, position);
    });
    return map;
  }, [shuffledOrder]);
  const letterVariants = {
    hidden: {
      opacity: 0,
      y: 8,
      filter: "blur(10px)",
      color: "#fff",
      textShadow:
        "0 0 14px rgba(255,255,255,0.95), 0 0 28px rgba(255,255,255,0.6)",
    },
    visible: (orderIndex = 0) => ({
      opacity: 1,
      y: 0,
      filter: ["blur(10px)", "blur(10px)", "blur(0px)"],
      color: ["#fff", "#fff", "hsl(var(--foreground))"],
      textShadow: [
        "0 0 14px rgba(255,255,255,0.95), 0 0 28px rgba(255,255,255,0.6)",
        "0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(255,255,255,0.4)",
        "0 0 0 rgba(0,0,0,0)",
      ],
      transition: {
        duration: 0.8,
        delay: orderIndex * 0.03,
        filter: {
          duration: 0.8,
          delay: orderIndex * 0.03,
          times: [0, 0.7, 1],
        },
        color: {
          duration: 0.8,
          delay: orderIndex * 0.03,
          times: [0, 0.85, 1],
        },
        textShadow: {
          duration: 0.8,
          delay: orderIndex * 0.03,
          times: [0, 0.7, 1],
        },
      },
    }),
    exit: (orderIndex = 0) => ({
      opacity: 0,
      y: -8,
      filter: ["blur(0px)", "blur(10px)"],
      color: ["hsl(var(--foreground))", "#fff"],
      textShadow: ["0 0 0 rgba(0,0,0,0)", "0 0 12px rgba(255,255,255,0.8)"],
      transition: {
        duration: 0.4,
        delay: orderIndex * 0.02,
        filter: { duration: 0.4, delay: orderIndex * 0.02 },
        color: { duration: 0.4, delay: orderIndex * 0.02 },
        textShadow: { duration: 0.4, delay: orderIndex * 0.02 },
      },
    }),
  };
  const containerVariants = {
    hidden: {},
    visible: {},
    exit: {},
  };

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentVideoIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section className="relative min-h-screen grid grid-rows-[auto_1fr] overflow-hidden pt-24 md:pt-28 lg:pt-32">
      {/* Enhanced Background with Gradient Mesh */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="w-full h-full" ref={emblaRef}>
          <div className="flex h-full">
            {videoContent?.map((video) => (
              <div key={video?.id} className="relative min-w-full h-full">
                <img
                  src={video?.thumbnail}
                  alt={video?.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
        {/* White wash overlay */}
        <div className="absolute inset-0 bg-white/60"></div>
      </div>

      {/* Enhanced Content */}
      <div className="relative z-10 container mx-auto px-6 mt-3 text-center text-foreground">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Enhanced Main Heading */}
          <div
            className={`space-y-6 animate-slide-up ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{ animationDelay: "0.2s" }}
          >
            <h1 className="text-fluid-5xl font-display font-bold leading-none tracking-tight">
              <span className="block mb-[2px]">Visual Storytelling</span>
            </h1>

            <div className="max-w-3xl mx-auto space-y-4">
              <p className="text-fluid-xl text-foreground/90 leading-relaxed font-light">
                {currentVideo?.description}
              </p>
              {/* <div className="flex items-center justify-center space-x-8 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={16} className="text-accent" />
                  <span>Award Winning</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} className="text-accent" />
                  <span>500+ Clients</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={16} className="text-accent" />
                  <span>5-Star Rated</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* Enhanced Video Controls */}
          <div
            className={`flex items-center justify-center space-x-6 animate-scale-in ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{ animationDelay: "0.4s" }}
          >
            <div className="text-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={titleKey}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="text-foreground font-medium text-lg mb-1"
                >
                  {letters.map((ch, i) => (
                    <motion.span
                      key={i}
                      variants={letterVariants}
                      custom={indexToDelay.get(i) ?? 0}
                      initial={{
                        color: "#fff",
                        filter: "blur(10px)",
                        textShadow:
                          "0 0 14px rgba(255,255,255,0.95), 0 0 28px rgba(255,255,255,0.6)",
                      }}
                      style={{
                        display: "inline-block",
                        whiteSpace: "pre",
                        color: "#fff",
                        filter: "blur(10px)",
                        textShadow:
                          "0 0 14px rgba(255,255,255,0.95), 0 0 28px rgba(255,255,255,0.6)",
                      }}
                    >
                      {ch}
                    </motion.span>
                  ))}
                </motion.div>
              </AnimatePresence>
              {/* <div className="text-muted-foreground text-sm">
                Now Playing • {currentVideoIndex + 1} of {videoContent?.length}
              </div> */}
            </div>
          </div>

          {/* Trust Indicators */}
          <div
            className={`flex flex-wrap items-center justify-center gap-8 text-muted-foreground text-sm animate-slide-up ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{ animationDelay: "0.8s" }}
          >
            {/* <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} />
              <span>Fully Insured</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} />
              <span>48hr Turnaround</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Zap" size={16} />
              <span>Same Day Response</span>
            </div> */}
          </div>
        </div>
      </div>

      {/* Sub-navigation for slide categories */}
      {/* CTA Buttons relocated just above the sub-nav */}
      <div className="absolute bottom-28 mb-8 left-0 right-0 z-20 pointer-events-auto w-full flex justify-center">
        <div className="px-6 w-full max-w-4xl flex justify-center">
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{ animationDelay: "0.6s" }}
          >
            <Button
              variant="default"
              size="xl"
              className="group bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-10 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              iconName="Play"
              iconPosition="left"
            >
              <span>Watch Our Reel</span>
              <div className="absolute inset-0 rounded-xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Button>

            <Button
              variant="outline"
              size="xl"
              asChild
              className="group border-2 border-border text-foreground hover:bg-muted/20 glass backdrop-blur-md px-10 py-4 rounded-xl hover:border-accent/50 hover:shadow-xl transition-all duration-300"
              iconName="Calendar"
              iconPosition="left"
            >
              <a
                href="#book-consultation"
                onClick={(e) => {
                  e.preventDefault();
                  if (!document.getElementById("MeetingsEmbedCode-js")) {
                    const s = document.createElement("script");
                    s.src =
                      "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
                    s.async = true;
                    s.defer = true;
                    s.id = "MeetingsEmbedCode-js";
                    document.body.appendChild(s);
                  }
                  let overlay = document.getElementById("hs-meetings-overlay");
                  if (!overlay) {
                    overlay = document.createElement("div");
                    overlay.id = "hs-meetings-overlay";
                    overlay.className =
                      "fixed inset-0 z-[60] flex justify-center items-start p-0 bg-[#425B76] md:bg-black/50";
                    overlay.style.overflow = "hidden";
                    overlay.style.overscrollBehavior = "contain";

                    const inner = document.createElement("div");
                    inner.className =
                      "relative w-screen h-screen md:w-full md:max-w-4xl md:h-[90vh] bg-[#425B76] md:bg-transparent rounded-none shadow-none p-0";
                    inner.style.overflow = "hidden";

                    const iframe = document.createElement("iframe");
                    iframe.src =
                      "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
                    iframe.style.width = "100%";
                    iframe.style.height = "100%";
                    iframe.style.minWidth = "312px";
                    iframe.style.minHeight = "0";
                    iframe.style.border = "none";
                    iframe.style.overflow = "hidden";
                    iframe.style.overscrollBehavior = "contain";
                    iframe.style.display = "block";
                    iframe.setAttribute("scrolling", "no");
                    iframe.setAttribute("data-hs-ignore", "true");

                    // Mobile-only stretch to remove inner gutters without blurring
                    const isMobile =
                      window.matchMedia("(max-width: 767px)").matches;
                    if (isMobile) {
                      iframe.style.position = "absolute";
                      iframe.style.width = "110vw";
                      iframe.style.height = "110vh";
                      iframe.style.left = "-5vw";
                      iframe.style.top = "-5vh";
                    } else {
                      iframe.style.position = "static";
                      iframe.style.width = "100%";
                      iframe.style.height = "100%";
                      iframe.style.left = "0";
                      iframe.style.top = "0";
                    }

                    const close = document.createElement("button");
                    close.className =
                      "fixed top-4 right-4 md:absolute md:top-[16px] md:right-[-3px] z-[70] text-gray-500 hover:text-gray-900 bg-white hover:bg-white/80 rounded";
                    close.innerText = "×";
                    close.style.fontSize = "18px";
                    close.style.lineHeight = "1";
                    close.style.margin = "0";
                    close.style.padding = "2px 9px";
                    close.style.paddingBottom = "6px";
                    close.onclick = () => {
                      document.body.style.overflow = "";
                      overlay.remove();
                    };
                    inner.appendChild(iframe);
                    inner.appendChild(close);
                    overlay.appendChild(inner);
                    document.body.appendChild(overlay);
                    document.body.style.overflow = "hidden";
                  }
                }}
              >
                <span>Book Consultation</span>
              </a>
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 z-20 pointer-events-auto w-full flex justify-center">
        <div className="px-6 w-full max-w-4xl flex justify-center">
          <div className="mx-auto inline-flex items-center gap-1 rounded-full border border-border bg-white/20 backdrop-blur-md shadow-lg px-2 py-1 mt-6 justify-center">
            {videoContent?.map((video, index) => (
              <button
                key={video?.id}
                onClick={() => handleVideoSelect(index)}
                className={`text-xs px-3 py-1 rounded-full transition-all duration-300 ${
                  index === currentVideoIndex
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                aria-label={`Switch to ${video?.title}`}
              >
                {video?.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <button
        type="button"
        className="absolute bottom-8 right-20 z-20 focus:outline-none"
        onClick={() => {
          // Scroll to next section or down by window height
          const nextSection = document.querySelector("[data-hero-next]");
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: "smooth" });
          } else {
            window.scrollBy({
              top: window.innerHeight,
              left: 0,
              behavior: "smooth",
            });
          }
        }}
        aria-label="Scroll down to explore more"
      >
        <div className="flex flex-col items-center space-y-5 text-muted-foreground group cursor-pointer hover:text-accent transition-colors duration-300">
          <span className="text-xs font-medium tracking-widest rotate-90 origin-center whitespace-nowrap mb-4">
            EXPLORE MORE
          </span>
          <Icon name="ChevronDown" size={24} className="animate-pulse pt-2" />
        </div>
      </button>

      {/* Floating Elements */}
      <div className="absolute top-1/4 left-8 w-2 h-2 bg-accent/40 rounded-full animate-float hidden lg:block"></div>
      <div
        className="absolute top-1/3 right-12 w-3 h-3 bg-white/20 rounded-full animate-float hidden lg:block"
        style={{ animationDelay: "2s" }}
      ></div>
      <div
        className="absolute bottom-1/3 left-16 w-1 h-1 bg-accent/60 rounded-full animate-float hidden lg:block"
        style={{ animationDelay: "4s" }}
      ></div>
    </section>
  );
};

export default HeroSection;
